/**
 * CSRF Token Utility
 * Generates and manages CSRF tokens for form submissions
 */

const CSRF_TOKEN_KEY = 'csrf_token';
const CSRF_TIMESTAMP_KEY = 'csrf_timestamp';
const CSRF_TOKEN_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

/**
 * Generates a random CSRF token
 * @returns {string} A cryptographically random token
 */
const generateCsrfToken = () => {
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const arr = new Uint8Array(32);
    crypto.getRandomValues(arr);
    return Array.from(arr, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }
  // Fallback for older browsers
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

/**
 * Gets or creates a CSRF token stored in sessionStorage
 * @returns {string} The current CSRF token
 */
export const getCsrfToken = () => {
  const stored = sessionStorage.getItem(CSRF_TOKEN_KEY);
  const timestamp = sessionStorage.getItem(CSRF_TIMESTAMP_KEY);
  const now = Date.now();

  // Check if token exists and hasn't expired
  if (stored && timestamp) {
    const tokenAge = now - parseInt(timestamp);
    if (tokenAge < CSRF_TOKEN_EXPIRY) {
      return stored;
    }
  }

  // Generate new token if expired or doesn't exist
  const newToken = generateCsrfToken();
  sessionStorage.setItem(CSRF_TOKEN_KEY, newToken);
  sessionStorage.setItem(CSRF_TIMESTAMP_KEY, String(now));

  return newToken;
};

/**
 * Validates a CSRF token
 * @param {string} token - The token to validate
 * @returns {boolean} Whether the token is valid
 */
export const validateCsrfToken = (token) => {
  const stored = sessionStorage.getItem(CSRF_TOKEN_KEY);
  const timestamp = sessionStorage.getItem(CSRF_TIMESTAMP_KEY);

  if (!stored || !timestamp) {
    return false;
  }

  const tokenAge = Date.now() - parseInt(timestamp);
  return token === stored && tokenAge < CSRF_TOKEN_EXPIRY;
};

/**
 * Refreshes the CSRF token
 * @returns {string} The new CSRF token
 */
export const refreshCsrfToken = () => {
  const newToken = generateCsrfToken();
  sessionStorage.setItem(CSRF_TOKEN_KEY, newToken);
  sessionStorage.setItem(CSRF_TIMESTAMP_KEY, String(Date.now()));
  return newToken;
};

/**
 * Clears the CSRF token (useful on logout)
 */
export const clearCsrfToken = () => {
  sessionStorage.removeItem(CSRF_TOKEN_KEY);
  sessionStorage.removeItem(CSRF_TIMESTAMP_KEY);
};
